import { useState, useEffect, useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Text, Button, Badge, Spinner, Input, Dropdown, Option, Switch } from '@fluentui/react-components';
import { ArrowLeftRegular, ServerRegular, ErrorCircleRegular } from '@fluentui/react-icons';
import { listEstate, confidenceStarsFromPercent, type EstateRow, type EstateListResult } from '../services/estate-service';
import { useApiToken } from '../hooks/useApiToken';
import { useSortableTable } from '../hooks/useSortableTable';
import { SortableHeader } from '../components/SortableHeader';
import { getBadgeProps, getConfidenceColor, SERVER_STATE_BADGE } from '../utils/badge-config';
import type { Project } from '../services/project-service';

const SERVER_STATES = ['discovered', 'assessed', 'designed', 'in-flight', 'migrated', 'excluded'];
const ALL_STATES = 'all';

type EstateSortColumn = 'hostName' | 'state' | 'lastSeenAt';

interface EstatePageProps {
  project: Project;
  onBack: () => void;
}

/**
 * Customer-scoped server estate (orchestrator RFC §4.5) — server-side paged,
 * filtered and sorted via EstateController. Estate rows are visible here only;
 * they never enter chat context or template variables (RFC §4.2).
 */
export function EstatePage({ project, onBack }: EstatePageProps) {
  const intl = useIntl();
  const getToken = useApiToken();

  const [result, setResult] = useState<EstateListResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<EstateRow | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [osInput, setOsInput] = useState('');
  const [osFilter, setOsFilter] = useState('');
  const [stateFilter, setStateFilter] = useState(ALL_STATES);
  const [projectOnly, setProjectOnly] = useState(true);
  const [page, setPage] = useState(1);

  const { sortColumn, sortDirection, handleSort } = useSortableTable<EstateSortColumn>({
    defaultColumn: 'hostName',
    onSortChange: () => setPage(1),
  });

  // Monotonic request sequence — overlapping fetches resolve out of order (higher
  // OFFSET pages are slower), and a late response must not overwrite a newer one.
  const seqRef = useRef(0);

  // Debounce free-text filters so each keystroke does not hit Cosmos.
  useEffect(() => {
    const timer = setTimeout(() => { setSearch(searchInput); setPage(1); }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);
  useEffect(() => {
    const timer = setTimeout(() => { setOsFilter(osInput); setPage(1); }, 300);
    return () => clearTimeout(timer);
  }, [osInput]);

  const loadEstate = useCallback(async () => {
    const seq = ++seqRef.current;
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const data = await listEstate(project.customerId, {
        state: stateFilter === ALL_STATES ? undefined : stateFilter,
        os: osFilter || undefined,
        projectId: projectOnly ? project.id ?? undefined : undefined,
        search: search || undefined,
        page,
        sort: sortColumn,
        dir: sortDirection,
      }, token);
      if (seq !== seqRef.current) return;
      setResult(data);
    } catch (err) {
      if (seq !== seqRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load the estate.');
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, [project.customerId, project.id, stateFilter, osFilter, projectOnly, search, page, sortColumn, sortDirection, getToken]);

  useEffect(() => { loadEstate(); }, [loadEstate]);

  // A filtered-to-zero estate is not an empty estate — the onboarding message would
  // mislead a user whose search merely excluded every row. projectOnly is part of the
  // default view, so it does not count as an active filter.
  const hasActiveFilters = search !== '' || osFilter !== '' || stateFilter !== ALL_STATES;
  const clearFilters = () => {
    setSearchInput('');
    setOsInput('');
    setStateFilter(ALL_STATES);
    setPage(1);
  };

  const items = result?.items ?? [];
  const total = result?.total ?? 0;
  const pageSize = result?.pageSize ?? 50;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const rangeFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeTo = Math.min(page * pageSize, total);

  const monthlyOf = (row: EstateRow): number | null => {
    const a = row.data?.latestAssessment;
    if (!a) return null;
    const sum = (a.monthlyComputeCost ?? 0) + (a.monthlyStorageCost ?? 0);
    return sum > 0 ? sum : null;
  };

  return (
    <div className="page-root page-root--scrollable estate-page">
      <div className="page-title-bar">
        <Button appearance="subtle" icon={<ArrowLeftRegular />} onClick={onBack} title={intl.formatMessage({ id: 'estate.backButton.title' })} />
        <ServerRegular className="icon-title text-brand" />
        <Text size={500} weight="semibold">{intl.formatMessage({ id: 'estate.title' }, { customer: project.customerId })}</Text>
      </div>

      <div className="page-filters">
        <Input
          className="page-filters-input"
          placeholder={intl.formatMessage({ id: 'estate.filter.search' })}
          value={searchInput}
          onChange={(_, d) => setSearchInput(d.value)}
        />
        <Dropdown
          className="page-filters-dropdown"
          value={stateFilter === ALL_STATES ? intl.formatMessage({ id: 'estate.filter.state.all' }) : stateFilter}
          selectedOptions={[stateFilter]}
          onOptionSelect={(_, d) => { setStateFilter(d.optionValue ?? ALL_STATES); setPage(1); }}
        >
          <Option value={ALL_STATES}>{intl.formatMessage({ id: 'estate.filter.state.all' })}</Option>
          {SERVER_STATES.map(s => <Option key={s} value={s}>{s}</Option>)}
        </Dropdown>
        <Input
          className="page-filters-input"
          placeholder={intl.formatMessage({ id: 'estate.filter.os' })}
          value={osInput}
          onChange={(_, d) => setOsInput(d.value)}
        />
        <Switch
          label={intl.formatMessage({ id: 'estate.filter.projectOnly' })}
          checked={projectOnly}
          onChange={(_, d) => { setProjectOnly(d.checked); setPage(1); }}
        />
      </div>

      {loading ? (
        <div className="page-loading"><Spinner size="large" label={intl.formatMessage({ id: 'estate.loading' })} /></div>
      ) : error ? (
        <div className="page-error">
          <ErrorCircleRegular />
          <Text className="text-danger">{error}</Text>
          <Button appearance="primary" onClick={loadEstate}>{intl.formatMessage({ id: 'estate.error.retry' })}</Button>
        </div>
      ) : items.length === 0 ? (
        hasActiveFilters ? (
          <div className="page-empty">
            <ServerRegular />
            <Text className="text-muted">{intl.formatMessage({ id: 'estate.empty.filtered' })}</Text>
            <Button size="small" onClick={clearFilters}>{intl.formatMessage({ id: 'estate.empty.clearFilters' })}</Button>
          </div>
        ) : (
          <div className="page-empty">
            <ServerRegular />
            <Text className="text-muted">{intl.formatMessage({ id: 'estate.empty' })}</Text>
          </div>
        )
      ) : (
        <>
          <div className="table-scroll-container">
            <table className="data-table estate-table">
              <thead>
                <tr>
                  <SortableHeader column="hostName" activeColumn={sortColumn} direction={sortDirection} onSort={handleSort}>
                    {intl.formatMessage({ id: 'estate.col.host' })}
                  </SortableHeader>
                  <SortableHeader column="state" activeColumn={sortColumn} direction={sortDirection} onSort={handleSort}>
                    {intl.formatMessage({ id: 'estate.col.state' })}
                  </SortableHeader>
                  <th>{intl.formatMessage({ id: 'estate.col.os' })}</th>
                  <th>{intl.formatMessage({ id: 'estate.col.cores' })}</th>
                  <th>{intl.formatMessage({ id: 'estate.col.memory' })}</th>
                  <th>{intl.formatMessage({ id: 'estate.col.target' })}</th>
                  <th>{intl.formatMessage({ id: 'estate.col.monthly' })}</th>
                  <th>{intl.formatMessage({ id: 'estate.col.confidence' })}</th>
                  <SortableHeader column="lastSeenAt" activeColumn={sortColumn} direction={sortDirection} onSort={handleSort}>
                    {intl.formatMessage({ id: 'estate.col.lastSeen' })}
                  </SortableHeader>
                </tr>
              </thead>
              <tbody>
                {items.map(row => {
                  const d = row.data;
                  if (!d) return null;
                  const stars = confidenceStarsFromPercent(d.latestAssessment?.confidencePercent);
                  const monthly = monthlyOf(row);
                  return (
                    <tr key={row.id} className="clickable" onClick={() => setSelected(prev => prev?.id === row.id ? null : row)}>
                      <td><Text weight="semibold">{d.hostName}</Text></td>
                      <td><Badge size="small" {...getBadgeProps(SERVER_STATE_BADGE, d.migrationState)}>{d.migrationState}</Badge></td>
                      <td className="text-muted">{d.operatingSystem ?? '—'}</td>
                      <td>{d.cores ?? '—'}</td>
                      <td>{d.memoryGb != null ? `${d.memoryGb} GB` : '—'}</td>
                      <td>{d.latestAssessment?.recommendedTarget ?? '—'}</td>
                      <td>{monthly != null ? monthly.toFixed(2) : '—'}</td>
                      <td>{stars != null
                        ? <Badge size="small" color={getConfidenceColor(stars)}>{'★'.repeat(stars)}</Badge>
                        : <span className="text-muted">—</span>}
                      </td>
                      <td className="text-muted">{d.lastSeenAt ? new Date(d.lastSeenAt).toLocaleDateString() : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="page-filters">
            <Text className="text-muted">
              {intl.formatMessage({ id: 'estate.pager.range' }, { from: rangeFrom, to: rangeTo, total })}
            </Text>
            <Button size="small" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>
              {intl.formatMessage({ id: 'estate.pager.prev' })}
            </Button>
            <Text className="text-muted">{intl.formatMessage({ id: 'estate.pager.page' }, { page, totalPages })}</Text>
            <Button size="small" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
              {intl.formatMessage({ id: 'estate.pager.next' })}
            </Button>
          </div>

          {selected?.data && (
            <ServerDetailPanel row={selected} onClose={() => setSelected(null)} />
          )}
        </>
      )}
    </div>
  );
}

function ServerDetailPanel({ row, onClose }: { row: EstateRow; onClose: () => void }) {
  const intl = useIntl();
  const d = row.data!;
  return (
    <div className="detail-panel">
      <div className="section-header">
        <Text size={400} weight="semibold">{d.hostName}</Text>
        <Badge size="small" {...getBadgeProps(SERVER_STATE_BADGE, d.migrationState)}>{d.migrationState}</Badge>
        <Button appearance="subtle" size="small" onClick={onClose}>{intl.formatMessage({ id: 'estate.detail.close' })}</Button>
      </div>
      <div className="detail-panel-content">
        <div className="meta-grid">
          <div className="meta-item"><span className="text-muted">{intl.formatMessage({ id: 'estate.detail.source' })}</span><span>{d.source ?? '—'}</span></div>
          <div className="meta-item"><span className="text-muted">{intl.formatMessage({ id: 'estate.detail.ips' })}</span><span>{d.ipAddresses?.length ? d.ipAddresses.join(', ') : '—'}</span></div>
          <div className="meta-item"><span className="text-muted">{intl.formatMessage({ id: 'estate.detail.storage' })}</span><span>{d.storageGb != null ? `${d.storageGb} GB` : '—'}</span></div>
          <div className="meta-item"><span className="text-muted">{intl.formatMessage({ id: 'estate.detail.projects' })}</span><span>{d.projectIds?.length ? d.projectIds.join(', ') : '—'}</span></div>
          <div className="meta-item"><span className="text-muted">{intl.formatMessage({ id: 'estate.detail.workload' })}</span><span>{d.workloadId ?? '—'}</span></div>
          <div className="meta-item"><span className="text-muted">{intl.formatMessage({ id: 'estate.detail.machineArmId' })}</span><span className="control-ref">{d.machineArmId ?? '—'}</span></div>
        </div>

        {d.latestAssessment && (
          <div className="flex-row flex-wrap">
            <Text weight="semibold">{intl.formatMessage({ id: 'estate.detail.latest.heading' })}</Text>
            <Text className="text-muted">{d.latestAssessment.runId}</Text>
            <Text>{d.latestAssessment.readiness ?? '—'}</Text>
            {(d.latestAssessment.issues?.length ?? 0) > 0 && (
              <Text className="text-warning">{d.latestAssessment.issues!.join('; ')}</Text>
            )}
          </div>
        )}

        {(d.stateHistory?.length ?? 0) > 0 && (
          <table className="data-table data-table--nested">
            <thead>
              <tr>
                <th>{intl.formatMessage({ id: 'estate.detail.history.col.state' })}</th>
                <th>{intl.formatMessage({ id: 'estate.detail.history.col.at' })}</th>
                <th>{intl.formatMessage({ id: 'estate.detail.history.col.by' })}</th>
                <th>{intl.formatMessage({ id: 'estate.detail.history.col.note' })}</th>
              </tr>
            </thead>
            <tbody>
              {[...d.stateHistory!].reverse().map((h, i) => (
                <tr key={`${h.at}-${i}`}>
                  <td><Badge size="small" {...getBadgeProps(SERVER_STATE_BADGE, h.state)}>{h.state}</Badge></td>
                  <td className="text-muted">{new Date(h.at).toLocaleString()}</td>
                  <td className="text-muted control-ref">{h.by}</td>
                  <td className="text-muted">{h.note ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
